import React, { useEffect, useState } from 'react';
import ContractWrapper from '../../../components/Contract/ContractWrapper';
import { useGetContract, usePutContract, usePostContract, useSetContractState } from '../hooks/api/contractDetailApi';
import useAppContext from '../../../hooks/context/useAppContext';
import PropTypes from 'prop-types';
import { isMonthlyPurchaseAvailableList, isBA } from './ContractEnum';
import { useGetCustomerEnum } from '../../../hooks/enums/useEnumHook';
import { formattedNumber } from '../../../utils/number';
import { useIntl } from 'react-intl';

const ContractDetail = ({ id }) => {
  const { getContract, data, loading, error } = useGetContract();
  const { postContract, data: postData, loading: postLoadingContract, error: postError } = usePostContract();
  const { putContract, data: putData, loading: putLoadingContract, error: putError } = usePutContract();
  const { postContractState, data: stateData, loading: stateLoading, error: stateError } = useSetContractState();

  const { getCustomerEnum, data: customerData } = useGetCustomerEnum();

  const [customersEnum, setCustomersEnum] = useState([]);
  const [contractId, setContractId] = useState(null);
  const [contractPeriod, setContractPeriod] = useState('');
  const [customerId, setCustomerId] = useState(-1);
  const [commodityId, setCommodityId] = useState('');
  const [commodityName, setCommodityName] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [maxPurchaseCount, setMaxPurchaseCount] = useState(0);
  const [isMonthlyPurchaseAvailable, setIsMonthlyPurchaseAvailable] = useState(2);
  const [idExternal, setIdExternal] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [invoicingCurrencyId, setInvoicingCurrencyId] = useState('');
  const [frameContract, setFrameContract] = useState('');
  const [supplyProductId, setSupplyProductId] = useState(1);
  const [quantities, setQuantities] = useState(0);
  const [apiResponse, setApiResponse] = useState(null);
  const [purchaseIndexTypeId, setPurchaseIndexTypeId] = useState('');
  const [indexValues, setIndexValues] = useState('');
  const [isBeskAksAvailable, setIsBeskAksAvailable] = useState(true);
  const [isSettlementAvailable, setIsSettlementAvailable] = useState(false);
  const [isSaleAvailable, setIsSaleAvailable] = useState(false);
  const [isFix, setIsFix] = useState(false);
  const [isAutoPurchase, setIsAutoPurchase] = useState(false);
  const [referencePrice, setReferencePrice] = useState(1.000);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [kspotPlus, setKSpotPlus] = useState();
  const [kspotMinus, setKSpotMinus] = useState();
  const [purchaseMethodId, setPurchaseMethodId] = useState('');
  const [purchaseLastDays, setPurchaseLastDays] = useState(null);
  const [state, setState] = useState(null);
  const { setBreadCrumbs } = useAppContext();
  const intl = useIntl();

  const newContractTitle = intl.formatMessage({ id: 'app.contract.new' });
  const editContractTitle = intl.formatMessage({ id: 'app.contract.edit' });
  const gasTitle = intl.formatMessage({ id: 'app.products.gas' });
  const eleTitle = intl.formatMessage({ id: 'app.products.ele' });

  const getBoolValueFromSelectField = (value) => {
    return value === 1;
  };

  const sendHandler = () => {
    const body =
      {
        idExternal: idExternal,
        purchaseIndexType: { id: purchaseIndexTypeId },
        supplyProduct: { id: supplyProductId },
        commodity: { id: commodityId },
        currency: { id: currencyId },
        customer: { id: customerId },
        state: apiResponse?.state || 1,
        contractPeriod: contractPeriod,
        isBeskAksAvailable: isBeskAksAvailable,
        isSettlementAvailable: isSettlementAvailable,
        isGuarantee: apiResponse?.isGuarantee || false,
        isMonthlyPurchaseAvailable: getBoolValueFromSelectField(isMonthlyPurchaseAvailable),
        isSaleAvailable: isSaleAvailable,
        isAutoPurchase: isAutoPurchase,
        frameContract: frameContract === '' ? null : frameContract,
        invoicingCurrency: {
          id: invoicingCurrencyId,
        },
        maxPurchaseCount: formattedNumber(maxPurchaseCount, 3),
        quantity: formattedNumber(quantity, 3),
        purchaseMethod: { id: purchaseMethodId },
        kspotPlus: kspotPlus === '' ? null : formattedNumber(kspotPlus, 3),
        kspotMinus: kspotMinus === '' ? null : formattedNumber(kspotMinus, 3),
        referencePrice: purchaseIndexTypeId === 'RP' ? formattedNumber(referencePrice, 3) : 1.000,
        quantities: quantities?.map((item) => {
          return { month: item.month, quantity: formattedNumber(item.quantity, 3) };
        }),
        purchaseLastDays: purchaseLastDays?.map((item) => {
          return {
            month: item.month, purchaseDay: item.date || item.purchaseDay,
          };
        }),
        indexValues: indexValues !== '' ? indexValues.map((item) => {
          return {
            priceSource: { id: item.priceSource.id }, purchaseIndexType: { id: item.purchaseIndexType.id },
            idTariff: item.idTariff, isSale: item.isSale,
            value: formattedNumber(item.value, 3),
            value2: formattedNumber(item.value2, 3),
          };
        }) : null,
      };
    if (id !== 'new') {
      Object.assign(body, { id: id });
      putContract(id, body);
    } else {
      postContract(body);
    }
  };

  const sendContractState = () => {
    const action = (state && state === 1) ? 'deactivate' : 'activate';
    postContractState(id, action);
    setState(state === 1 ? 0 : 1);
  };

  const contract = {
    id,
    state,
    contractId,
    contractPeriod,
    quantity,
    maxPurchaseCount,
    idExternal,
    quantities,
    indexValues,
    commodityName,
    totalQuantity,
    isBeskAksAvailable,
    isSettlementAvailable,
    isSaleAvailable,
    isFix,
    isAutoPurchase,
    purchaseIndexTypeId,
    isMonthlyPurchaseAvailable,
    isMonthlyPurchaseAvailableList,
    isBA,
    customerId,
    commodityId,
    currencyId,
    referencePrice,
    kspotPlus,
    kspotMinus,
    supplyProductId,
    purchaseLastDays,
    purchaseMethodId,
    customersEnum,
    putData,
    putLoadingContract,
    postData,
    putError,
    postError,
    postLoadingContract,
    stateData,
    stateLoading,
    stateError,
    frameContract,
    invoicingCurrencyId,
  };

  const handlers = {
    setContractPeriod,
    setQuantity,
    setMaxPurchaseCount,
    setIsMonthlyPurchaseAvailable,
    setIdExternal,
    setQuantities,
    setIndexValues,
    sendHandler,
    setCustomerId,
    setCommodityId,
    setCurrencyId,
    setReferencePrice,
    setSupplyProductId,
    setPurchaseLastDays,
    setPurchaseIndexTypeId,
    setIsBeskAksAvailable,
    setIsSettlementAvailable,
    setIsSaleAvailable,
    setIsFix,
    setIsAutoPurchase,
    setPurchaseMethodId,
    sendContractState,
    setState,
    setInvoicingCurrencyId,
    setFrameContract,
    setKSpotPlus,
    setKSpotMinus,
  };

  useEffect(() => {
    if (id !== 'new') {
      (async () => {
        await getContract(id);
      })();
    }
  }, [id, getContract]);

  useEffect(() => {
    (async () => {
      await getCustomerEnum();
    })();
  }, [getCustomerEnum]);

  useEffect(() => {
    if (customerData) {
      setCustomersEnum(customerData);
    }
  }, [customerData]);

  useEffect(() => {
    if (data) {
      let comodityTitle = eleTitle;
      if (data?.commodity.id === 'GAS_THE' || data?.commodity.id === 'GAS_CZVTP') {
        comodityTitle = gasTitle;
      }
      setBreadCrumbs(`${editContractTitle} ${comodityTitle} ${data?.contractPeriod}`);
    } else if (id === 'new') {
      setBreadCrumbs(`${newContractTitle}`);
    }
  }, [data, id, editContractTitle, eleTitle, gasTitle, newContractTitle, setBreadCrumbs]);

  useEffect(() => {
    if (data) {
      setContractId(data.id);
      setPurchaseMethodId(data.purchaseMethod.id);
      setApiResponse(data);
      setReferencePrice(data.referencePrice);
      setState(data?.state);
      setCurrencyId(data.currency.id);
      setSupplyProductId(data.supplyProduct.id);
      setContractPeriod(data.contractPeriod);
      setCustomerId(data.customer.id);
      setCommodityId(data.commodity.id);
      setQuantity(data.quantity);
      setCommodityName(data.commodity.name);
      setMaxPurchaseCount(data.maxPurchaseCount);
      setIsMonthlyPurchaseAvailable(data.isMonthlyPurchaseAvailable === true ? 1 : 2);
      setIdExternal(data.idExternal);
      setQuantities(data.quantities);
      setIndexValues(data.indexValues);
      setPurchaseLastDays(data.purchaseLastDays);
      setIsBeskAksAvailable(!!data.isBeskAksAvailable);
      setIsSettlementAvailable(!!data.isSettlementAvailable);
      setIsSaleAvailable(!!data.isSaleAvailable);
      setIsFix(!!data.isFix);
      setIsAutoPurchase(!!data.isAutoPurchase);
      setPurchaseIndexTypeId(data.purchaseIndexType.id);
      setTotalQuantity(data.quantities.reduce((sum, item) => {
        return sum + item.quantity;
      }, 0));

      setInvoicingCurrencyId(data.invoicingCurrency.id);
      setFrameContract(data.frameContract === null ? '' : data.frameContract);
      setKSpotPlus(data.kspotPlus === null ? '' : data.kspotPlus);
      setKSpotMinus(data.kspotMinus === null ? '' : data.kspotMinus);
    }
  }, [data]);

  useEffect(() => {
    if (putData) {
      setApiResponse(putData);
    }
  }, [putData]);

  return (
    <>
      <ContractWrapper
        id={id}
        loading={loading}
        error={error}
        contract={contract}
        handlers={handlers}
      />
    </>
  );
};

ContractDetail.propTypes = {
  id: PropTypes.string,
};
export default ContractDetail;
